import { isAxiosError, type AxiosResponse } from 'axios';
import type {
    Loader,
    LoaderCallbacks,
    LoaderConfiguration,
    LoaderContext,
    LoaderStats,
} from 'hls.js';
import { viinexClient } from '@/services/viinex-client';

export class ViinexHlsLoader<
    Context extends LoaderContext = LoaderContext,
> implements Loader<Context> {
    context: Context | null = null;
    stats: LoaderStats = {
        aborted: false,
        loaded: 0,
        retry: 0,
        total: 0,
        chunkCount: 0,
        bwEstimate: 0,
        loading: { start: 0, first: 0, end: 0 },
        parsing: { start: 0, end: 0 },
        buffering: { start: 0, first: 0, end: 0 },
    };

    private controller = new AbortController();
    private callbacks: LoaderCallbacks<Context> | null = null;
    private response: AxiosResponse<string | ArrayBuffer> | null = null;
    private retryTimer: ReturnType<typeof setTimeout> | null = null;

    load(
        context: Context,
        config: LoaderConfiguration,
        callbacks: LoaderCallbacks<Context>,
    ): void {
        this.context = context;
        this.callbacks = callbacks;
        this.stats.loading.start = performance.now();
        void this.loadRequest(context, config);
    }

    abort(): void {
        if (this.stats.aborted) return;
        if (this.retryTimer !== null) clearTimeout(this.retryTimer);
        this.retryTimer = null;
        this.controller.abort();
        if (this.response !== null) return;
        this.stats.aborted = true;
        if (this.context) {
            this.callbacks?.onAbort?.(this.stats, this.context, undefined);
        }
    }

    destroy(): void {
        this.callbacks = null;
        this.abort();
        this.context = null;
        this.response = null;
    }

    getResponseHeader(name: string): string | null {
        const value = this.response?.headers[name.toLowerCase()];
        return value == null ? null : String(value);
    }

    getCacheAge(): number | null {
        const age = this.getResponseHeader('age');
        return age !== null && Number.isFinite(Number(age))
            ? Number(age)
            : null;
    }

    private async loadRequest(
        context: Context,
        config: LoaderConfiguration,
    ): Promise<void> {
        if (this.stats.aborted) return;
        let response: AxiosResponse<string | ArrayBuffer>;
        try {
            response = await viinexClient.getResponse<string | ArrayBuffer>(
                context.url,
                {
                    responseType:
                        context.responseType === 'arraybuffer'
                            ? 'arraybuffer'
                            : 'text',
                    signal: this.controller.signal,
                    timeout: Number.isFinite(config.loadPolicy.maxLoadTimeMs)
                        ? config.loadPolicy.maxLoadTimeMs
                        : 0,
                    headers: {
                        ...context.headers,
                        // hls.js sets rangeEnd to 0 when it wants the whole segment.
                        ...(!!context.rangeEnd && {
                            Range: `bytes=${context.rangeStart ?? 0}-${context.rangeEnd - 1}`,
                        }),
                    },
                    onDownloadProgress: ({ loaded, total, event }) => {
                        if (this.stats.aborted) return;
                        const request = event?.target;
                        if (
                            request instanceof XMLHttpRequest &&
                            request.status === 401
                        )
                            return;
                        this.stats.loading.first ||= performance.now();
                        this.stats.loaded = loaded;
                        this.stats.total = total ?? loaded;
                    },
                },
            );
        } catch (error) {
            if (this.stats.aborted) return;
            this.handleRequestError(error, context, config);
            return;
        }
        if (this.stats.aborted) return;

        this.response = response;
        this.stats.loading.first ||= performance.now();
        this.stats.loading.end = performance.now();
        const data = response.data;
        this.stats.loaded = this.stats.total =
            typeof data === 'string' ? data.length : data.byteLength;
        this.stats.bwEstimate =
            (this.stats.total * 8000) /
            Math.max(1, this.stats.loading.end - this.stats.loading.first);
        this.callbacks?.onProgress?.(
            this.stats,
            context,
            data,
            response.request,
        );
        this.callbacks?.onSuccess(
            {
                url: response.request?.responseURL ?? context.url,
                data,
                code: response.status,
            },
            this.stats,
            context,
            response.request,
        );
    }

    private handleRequestError(
        error: unknown,
        context: Context,
        config: LoaderConfiguration,
    ): void {
        const response = isAxiosError(error) ? error.response : undefined;
        const code = response?.status ?? 0;
        const networkDetails = isAxiosError(error) ? error.request : undefined;
        const timedOut =
            isAxiosError(error) &&
            (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT');
        const retryPolicy = timedOut
            ? config.loadPolicy.timeoutRetry
            : config.loadPolicy.errorRetry;
        const defaultRetry =
            retryPolicy !== null &&
            this.stats.retry < retryPolicy.maxNumRetry &&
            (timedOut ||
                (code !== 0 && (code < 400 || code > 499)) ||
                (code === 0 && !navigator.onLine));
        const retry = retryPolicy?.shouldRetry
            ? retryPolicy.shouldRetry(
                  retryPolicy,
                  this.stats.retry,
                  timedOut,
                  { url: context.url, code },
                  defaultRetry,
              )
            : defaultRetry;

        if (retry && retryPolicy) {
            const delay = Math.min(
                retryPolicy.maxRetryDelayMs,
                retryPolicy.retryDelayMs *
                    (retryPolicy.backoff === 'linear'
                        ? 1
                        : 2 ** this.stats.retry),
            );
            this.stats.retry += 1;
            this.retryTimer = setTimeout(() => {
                this.retryTimer = null;
                void this.loadRequest(context, config);
            }, delay);
            return;
        }
        if (timedOut) {
            this.callbacks?.onTimeout(this.stats, context, networkDetails);
            return;
        }
        this.callbacks?.onError(
            {
                code,
                text:
                    error instanceof Error
                        ? error.message
                        : 'HLS request failed',
            },
            context,
            networkDetails,
            this.stats,
        );
    }
}
